import React, { useState, useEffect } from "react";
import API_URL from "../config";
import api from '../components/setupAxios';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Box, TextField, Button, ButtonGroup,IconButton, toggleRowMenu } from '@mui/material';
import MenuIcon from "@mui/icons-material/Menu";

const AdminDashboard = () => {
  const [labs, setLabs] = useState([]);
  const [form, setForm] = useState({ name: "", location: "", capacity: "" });
  const [editingLabId, setEditingLabId] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [openLabId, setOpenLabId] = useState(null);

  const adjustDateToDayOfWeek = (timeString, targetDay, referenceTimeString = null) => {
    const time = new Date(timeString);
    const now = new Date();

    const diff = (targetDay - now.getDay() + 7) % 7;
    let eventDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + diff,
      time.getHours(),
      time.getMinutes(),
      time.getSeconds()
    );

    if (referenceTimeString) {
      const refTime = new Date(referenceTimeString);
      const refMinutes = refTime.getHours() * 60 + refTime.getMinutes();
      const eventMinutes = eventDate.getHours() * 60 + eventDate.getMinutes();
      if (eventMinutes <= refMinutes) {
        eventDate.setDate(eventDate.getDate() + 1);
      }
    }
    return eventDate.toISOString();
  };
  
  // Fetch labs on mount
  useEffect(() => {
    fetchLabs();  
    fetch(`${API_URL}/lab-schedules`) 
            .then(res => res.json())
            .then(data => {
                console.log("Fetched schedule data:", data);
                setSchedule(data);
            })
            .catch(err => console.error("Error fetching schedules:", err));
  }, []);

  // Fetch lab data from backend
  const fetchLabs = async () => {
    try {
      const response = await api.get("/lab"); // Update with full backend URL
      setLabs(response.data);
    } catch (error) {
      console.error("Error fetching labs:", error);
    }
  };

  const mergedSchedules = schedule.reduce((acc, curr) => {
    const key = `${adjustDateToDayOfWeek(curr.startTime, curr.dayOfWeek)}-${adjustDateToDayOfWeek(curr.endTime, curr.dayOfWeek)}`;
    
    if (!acc[key]) {
        acc[key] = { 
          locations: new Set(), 
          startTime: curr.startTime, 
          endTime: curr.endTime, 
          dayOfWeek: curr.dayOfWeek,
          isActive: curr.isActive  
        };
    }

    acc[key].locations.add(curr.lab?.location || "Unknown Location");
    return acc;
}, {});

const formattedEvents = Object.values(mergedSchedules)
.filter(event => event.isActive)
.map(event => ({
    title: Array.from(event.locations).join(", "), 
    start: adjustDateToDayOfWeek(event.startTime, event.dayOfWeek),
    end: adjustDateToDayOfWeek(event.endTime, event.dayOfWeek)
}));

  // Handle form input change
  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Create a new lab
  const createLab = async () => {
    try {
      if (!form.name.trim() || !form.location.trim() || !form.capacity) {
        alert("Please fill in all fields before creating a lab.");
        return;
    }

      await api.post("/lab/create", form, 
      {headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }}); // Update with full backend URL
      fetchLabs();
      setForm({ name: "", location: "", capacity: "" });
    } catch (error) {
      console.error("Error creating lab:", error);
    }
  };

  // Update an existing lab
  const updateLab = async () => {
    try {
      if (!form.name.trim() || !form.location.trim() || !form.capacity) {
        alert("Please fill in all fields before updating the lab.");
        return;
    }
    
      await api.put(`/lab/edit/${editingLabId}`, form, 
      {headers: { Authorization: `Bearer ${localStorage.getItem("token")}`}}); // Update with full backend URL
      fetchLabs();
      setForm({ name: "", location: "", capacity: "" });
      setEditingLabId(null);
    } catch (error) {
      console.error("Error updating lab:", error);
    }
  };

  // Delete a lab
  const deleteLab = async (id) => {
    if (window.confirm("Are you sure you want to delete this lab?")) {
      try {
        await api.delete(`/lab/delete/${id}`, 
        {headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }}); // Update with full backend URL
        fetchLabs();
      } catch (error) {
        console.error("Error deleting lab:", error);
      }
    }
  };

  // Handle editing a lab
  const handleEdit = (lab) => {
    setForm({ name: lab.name, location: lab.location, capacity: lab.capacity });
    setEditingLabId(lab._id);
  };

  // Cancel editing
  const cancelEdit = () => {
    setForm({ name: "", location: "", capacity: "" });
    setEditingLabId(null);
  };

const handleExportAllXlsx = async () => {
  try {
    const response = await api.get('/admin/export-all-xlsx', {
      responseType: 'blob',
      headers: {
        Authorization:  `Bearer ${localStorage.getItem("token")}`
      } 
    });

    // Convert the binary response to a Blob for Excel
    const file = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const fileURL = URL.createObjectURL(file);

    // Create a temporary <a> element to trigger the download
    const link = document.createElement('a');
    link.href = fileURL;
    link.download = 'openlab_export.xlsx'; // Downloaded file name
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up memory
    URL.revokeObjectURL(fileURL);
  } catch (error) {
    console.error('Export all xlsx failed:', error);
    alert('Failed to export data to Excel!');
  }
};

const toggleRowMenu = (labId) => {
  if (openLabId === labId) {
    setOpenLabId(null);
  } else {
    setOpenLabId(labId);
  }
};

return (
  <div className="dashboard-container">
    <div className="calendar-container">
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin]}
        initialView="timeGridWeek"
        slotMinTime="08:00:00"
        slotMaxTime="23:00:00"
        events={formattedEvents}
        allDaySlot={false}
      />
    </div>

    <div className="session-container">
      <div>
        <TextField
          variant="outlined"
          size="small"
          label="Lab Name"
          name="name"
          value={form.name}
          onChange={handleInputChange}
          style={{ marginRight: "8px" }}
        />
        <TextField
          variant="outlined"
          size="small"
          label="Location"
          name="location"
          value={form.location}
          onChange={handleInputChange}
          style={{ marginRight: "8px" }}
        />
        <TextField
          variant="outlined"
          size="small"
          label="Capacity"
          name="capacity"
          type="number"
          value={form.capacity}
          onChange={handleInputChange}
          style={{ marginRight: "8px" }}
        />
        {editingLabId ? (
          <Box display="flex" justifyContent="center" mt={2} mb={2}>
            <ButtonGroup variant="contained" fullWidth>
              <Button onClick={updateLab}>Update</Button>
              <Button onClick={cancelEdit}>Cancel</Button>
            </ButtonGroup>
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" mt={2} mb={2}>
            <Button
              onClick={createLab}
              variant="contained"
              sx={{ width: "50%" }}
            >
              Create Lab
            </Button>
          </Box>
        )}
      </div>

      <div className="card">
        <table className="manage-student-table">
          <thead>
            <tr>
              <th>Lab Details</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {labs.map((lab) => (
              <tr key={lab._id}>
                <td>
                  {lab.name} - {lab.location} - {lab.capacity} seats
                </td>
                <td>
                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    <ButtonGroup variant="contained">
                      <Button
                        onClick={() =>
                          (window.location.href = `/layout-design/${lab._id}`)
                        }
                      >
                        Layout
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/schedule/${lab._id}`)
                        }
                      >
                        Schedule
                      </Button>
                      <Button onClick={() => handleEdit(lab)}>Edit</Button>
                      <Button onClick={() => deleteLab(lab._id)}>Delete</Button>
                    </ButtonGroup>
                  </Box>

                  <Box
                    sx={{
                      display: { xs: "inline-block", md: "none" },
                      position: "relative",
                    }}
                  >
                    <IconButton onClick={() => toggleRowMenu(lab._id)}>
                      <MenuIcon />
                    </IconButton>
                    {openLabId === lab._id && (
                      <Box
                        sx={{
                          position: "absolute",
                          right: 0,
                          backgroundColor: "#fff",
                          boxShadow: 1,
                          borderRadius: 1,
                          zIndex: 10,
                        }}
                      >
                        <Button
                          sx={{ width: "100%" }}
                          onClick={() =>
                            (window.location.href = `/layout-design/${lab._id}`)
                          }
                        >
                          Layout
                        </Button>
                        <Button
                          sx={{ width: "100%" }}
                          onClick={() =>
                            (window.location.href = `/schedule/${lab._id}`)
                          }
                        >
                          Schedule
                        </Button>
                        <Button
                          sx={{ width: "100%" }}
                          onClick={() => handleEdit(lab)}
                        >
                          Edit
                        </Button>
                        <Button
                          sx={{ width: "100%" }}
                          onClick={() => deleteLab(lab._id)}
                        >
                          Delete
                        </Button>
                      </Box>
                    )}
                  </Box>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Button onClick={handleExportAllXlsx}>Export All Data to Excel</Button>
    </div>
  </div>
);

};

export default AdminDashboard;
